import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type {
  Application,
  Deployment,
  Device,
  DeviceAlert,
  DeviceSummary,
  ProviderInfo,
} from '../lib/api';
import {
  DeploymentStatusBadge,
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  Panel,
  PolicyStatusBadge,
  ReachabilityBadge,
  relativeTime,
  useAsync,
} from '../components/ui';
import { Donut, MetricCard } from '../components/chrome';
import {
  IconChart,
  IconPlus,
  IconRocket,
  IconTablet,
  IconUpload,
  IconWarn,
  IconWifi,
  IconWifiOff,
} from '../components/icons';

const ALERT_TITLES: Record<string, string> = {
  'device.offline': 'Device offline',
  'device.checkin_delayed': 'Device may be offline',
  'device.reconnected': 'Device reconnected',
  'device.possible_factory_reset': 'Possible factory reset',
};

export default function DashboardPage() {
  const devices = useAsync(
    (signal) => api.get<{ devices: Device[]; summary: DeviceSummary }>('/devices', signal),
    [],
    15_000,
  );
  const apps = useAsync(
    (signal) => api.get<{ applications: Application[] }>('/applications', signal),
    [],
  );
  const deployments = useAsync(
    (signal) => api.get<{ deployments: Deployment[] }>('/deployments', signal),
    [],
    15_000,
  );
  // Device state alerts used to appear in the audit log. They are observations
  // rather than actions, so they now have their own feed - but they still have
  // to be visible somewhere, or moving them would have quietly lost them.
  const alerts = useAsync(
    (signal) =>
      api.get<{ enabled: boolean; alerts: DeviceAlert[] }>('/alerts?openOnly=true&limit=8', signal),
    [],
    60_000,
  );
  const provider = useAsync(
    (signal) => api.get<{ active: ProviderInfo }>('/providers/active', signal),
    [],
  );

  const summary = devices.data?.summary;
  const total = summary?.total ?? 0;
  const online = summary?.online ?? 0;
  const offline = summary?.offline ?? 0;
  const attention = summary?.needsAttention ?? 0;
  const feed = alerts.data?.alerts ?? [];
  const latestApps = (apps.data?.applications ?? []).slice(0, 5);
  const recentDeployments = (deployments.data?.deployments ?? []).slice(0, 5);
  const needsAttention = (devices.data?.devices ?? []).filter(
    (d) => d.lastError || d.managementState !== 'device_owner' || d.policy?.status === 'pending',
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Dashboard</h2>
          <p>Live status of every enrolled tablet.</p>
        </div>
        <div className="actions">
          <Link className="button primary" to="/enrollment"><IconPlus /> Add device</Link>
          <Link className="button btn-purple" to="/applications"><IconUpload /> Upload APK</Link>
          <Link className="button btn-green" to="/deployments"><IconRocket /> Deploy update</Link>
        </div>
      </div>

      <ErrorNotice error={devices.error} />

      {provider.data && !provider.data.active.available && (
        <Notice kind="warning">
          <strong>Device management provider unavailable: {provider.data.active.displayName}.</strong>
          <div>{provider.data.active.reason}</div>
        </Notice>
      )}

      {provider.data?.active.available && !provider.data.active.capabilities.survivesFactoryReset && (
        <Notice kind="info">
          <strong>Factory reset does not preserve management on this provider.</strong>
          <div>{provider.data.active.capabilities.factoryResetNote}</div>
        </Notice>
      )}

      <div className="metric-row">
        <MetricCard tone="blue" icon={<IconTablet />} label="Total Devices" value={summary?.total ?? '—'} />
        <MetricCard tone="green" icon={<IconWifi />} label="Online" value={summary?.online ?? '—'} />
        <MetricCard tone="red" icon={<IconWifiOff />} label="Offline" value={summary?.offline ?? '—'} />
        <MetricCard tone="orange" icon={<IconWarn />} label="Needs Attention" value={summary?.needsAttention ?? '—'} />
      </div>

      <div className="quick-row">
        <Link className="quick-card blue" to="/enrollment">
          <IconPlus />
          <span>Add Device</span>
        </Link>
        <Link className="quick-card purple" to="/applications">
          <IconUpload />
          <span>Upload APK</span>
        </Link>
        <Link className="quick-card green" to="/deployments">
          <IconRocket />
          <span>Deploy Update</span>
        </Link>
        <Link className="quick-card sky" to="/audit-logs">
          <IconChart />
          <span>View Reports</span>
        </Link>
      </div>

      <div className="grid cols-2">
        <Panel title="Device status">
          {devices.loading && !devices.data ? (
            <Loading what="status" />
          ) : total === 0 ? (
            <Empty title="No devices yet.">
              <Link to="/enrollment">Generate an enrollment token</Link> to add one.
            </Empty>
          ) : (
            <div className="status-split">
              <Donut online={online} offline={offline} />
              <ul className="legend">
                <li><i className="dot ok" /> Online <b>{online}</b> <span>{pct(online, total)}</span></li>
                <li><i className="dot danger" /> Offline <b>{offline}</b> <span>{pct(offline, total)}</span></li>
                <li><i className="dot warn" /> Needs attention <b>{attention}</b> <span>{pct(attention, total)}</span></li>
              </ul>
            </div>
          )}
        </Panel>

        <Panel title="Recent activity">
          {alerts.loading ? (
            <Loading />
          ) : feed.length === 0 ? (
            <Empty title="No recent activity.">Alerts will appear here as devices check in.</Empty>
          ) : (
            <ol className="activity">
              {feed.map((a) => (
                <li key={a.id} data-severity={a.severity}>
                  <i />
                  <div>
                    <strong>{ALERT_TITLES[a.alertType] ?? 'Device state changed'}</strong>
                    <div className="small muted">
                      {a.studentName ?? a.deviceLabel ?? 'Unnamed device'}
                      {a.studentRef ? ` (${a.studentRef})` : ''}
                    </div>
                  </div>
                  <time>{relativeTime(a.createdAt)}</time>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>

      <Panel
        title="Device alerts"
        actions={
          alerts.data && !alerts.data.enabled ? <span className="badge muted">Alerts off</span> : null
        }
      >
        {alerts.loading ? (
          <Loading />
        ) : !alerts.data?.enabled ? (
          <p className="small muted" style={{ margin: 0 }}>
            Device state alerts are switched off. Turn them back on in Settings.
          </p>
        ) : feed.length === 0 ? (
          <Empty title="No active alerts.">Every device is checking in as expected.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {feed.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="device-cell">
                        <span className="model">{a.deviceLabel ?? 'Tablet'}</span>
                        {a.studentName && <span className="serial">{a.studentName}</span>}
                      </div>
                    </td>
                    {/* The severity the server actually assigned. Printing a
                        fixed "Warning" here would flatten an info notice and a
                        critical one into the same signal. */}
                    <td>
                      <span className={`badge dot ${
                        a.severity === 'critical' || a.severity === 'high'
                          ? 'danger'
                          : a.severity === 'warning'
                            ? 'warn'
                            : 'muted'
                      }`}>
                        {a.severity}
                      </span>
                    </td>
                    <td>{ALERT_TITLES[a.alertType] ?? 'Device state changed'}</td>
                    <td className="small muted">{relativeTime(a.createdAt)}</td>
                    <td>
                      <Link className="button small" to={`/devices/${a.deviceId}`}>View device</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Only rendered when something is actually wrong, so its presence on the
          page is itself the signal. */}
      {needsAttention.length > 0 && (
        <Panel title="Needs attention">
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Device</th><th>Issue</th><th>Last seen</th></tr>
              </thead>
              <tbody>
                {needsAttention.map((d) => (
                  <tr key={d.id}>
                    <td><Link to={`/devices/${d.id}`}>{d.displayName}</Link></td>
                    <td>
                      {d.lastError
                        ? d.lastError.message
                        : d.managementState !== 'device_owner'
                          ? `Not a Device Owner (reported: ${d.managementState}). Kiosk restrictions cannot be enforced.`
                          : 'Assigned a policy it has not applied yet.'}
                    </td>
                    <td className="small muted">{relativeTime(d.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <div className="grid cols-2">
        <Panel title="Devices">
          {devices.loading && !devices.data ? (
            <Loading what="devices" />
          ) : (devices.data?.devices.length ?? 0) === 0 ? (
            <Empty>
              No devices enrolled yet. <Link to="/enrollment">Generate an enrollment token</Link> to add one.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Name</th><th>Status</th><th>Policy</th><th>Last seen</th></tr>
                </thead>
                <tbody>
                  {devices.data!.devices.slice(0, 8).map((d) => (
                    <tr key={d.id}>
                      <td>
                        <Link to={`/devices/${d.id}`}>{d.displayName}</Link>
                        <div className="small muted">{d.model} · Android {d.androidVersion}</div>
                      </td>
                      <td><ReachabilityBadge reachability={d.reachability} /></td>
                      <td>
                        {d.policy
                          ? <PolicyStatusBadge status={d.policy.status} />
                          : <span className="badge muted">NONE</span>}
                      </td>
                      <td className="small muted">{relativeTime(d.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Applications">
          {apps.loading && !apps.data ? (
            <Loading what="applications" />
          ) : latestApps.length === 0 ? (
            <Empty>
              No applications yet. <Link to="/applications">Upload an APK</Link>.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Application</th><th>Latest</th><th className="num">Installed on</th></tr>
                </thead>
                <tbody>
                  {latestApps.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <Link to={`/applications/${a.id}`}>{a.display_name}</Link>
                        <div className="small muted mono">{a.package_name}</div>
                      </td>
                      <td>
                        {a.latest_version_name ? `v${a.latest_version_name}` : '—'}
                        <div className="small muted">code {a.latest_version_code ?? '—'}</div>
                      </td>
                      <td className="num">{a.installed_device_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Recent deployments">
        {deployments.loading && !deployments.data ? (
          <Loading what="deployments" />
        ) : recentDeployments.length === 0 ? (
          <Empty>No deployments yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Application</th><th>Version</th><th>Status</th>
                  <th className="num">Installed</th><th className="num">Failed</th>
                  <th className="num">In flight</th><th>Started</th>
                </tr>
              </thead>
              <tbody>
                {recentDeployments.map((d) => (
                  <tr key={d.id}>
                    <td><Link to={`/deployments/${d.id}`}>{d.application_name}</Link></td>
                    <td>v{d.version_name}</td>
                    <td><DeploymentStatusBadge status={d.status} /></td>
                    <td className="num">{d.success_count}/{d.target_count}</td>
                    <td className="num">{d.failure_count}</td>
                    <td className="num">{d.in_flight_count ?? 0}</td>
                    <td className="small muted">{relativeTime(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

function pct(n: number, total: number) {
  if (!total) return '0%';
  return `${Math.round((n / total) * 100)}%`;
}
